<div hlmCard>
  @if (loading) {
    <div class="space-y-4 p-4" aria-busy="true">
      @for (row of skeletonRows; track row) {
        <div class="flex items-center gap-4">
          <hlm-skeleton class="h-4 w-28 shrink-0" />
          <hlm-skeleton class="h-4 w-24 shrink-0" />
          <hlm-skeleton class="hidden h-4 w-20 shrink-0 md:block" />
          <hlm-skeleton class="hidden h-4 min-w-0 flex-1 md:block" />
          <hlm-skeleton class="ms-auto h-6 w-20 shrink-0 rounded-full" />
        </div>
      }
    </div>
  } @else if (!vouchers || vouchers.length === 0) {
    <hlm-empty class="py-10">
      <div hlmEmptyHeader>
        <div hlmEmptyMedia variant="icon"><ng-icon name="lucideTicket" /></div>
        <p hlmEmptyTitle>Keine Gutscheine</p>
        <p hlmEmptyDescription>Für diesen Zeitraum wurden keine Gutscheine gefunden.</p>
      </div>
    </hlm-empty>
  } @else {
    <!-- Mobile: card list with a sort menu, the table needs too much width -->
    <div class="md:hidden">
      <div class="flex items-center justify-between px-4 pb-2">
        <p class="text-muted-foreground text-xs">{{ totalItems }} Gutscheine</p>
        <button hlmBtn variant="outline" size="xs" [hlmDropdownMenuTrigger]="sortMenu" align="end">
          <ng-icon name="lucideArrowUpDown" />
          Sortieren
        </button>
      </div>

      <div hlmItemGroup class="px-2">
        @for (voucher of vouchers; track voucher.id) {
          <button hlmItem size="sm" class="text-start" (click)="viewVoucherDetails(voucher.code)">
            <div hlmItemContent>
              <span hlmItemTitle class="font-mono">{{ voucher.code || '–' }}</span>
              <span hlmItemDescription>
                {{ voucher.recipient_email }} · {{ formatDate(voucher.created_at) }}
              </span>
              <div class="mt-1 flex flex-wrap items-center gap-1">
                <span hlmBadge variant="secondary">
                  <ng-icon [name]="statusIcon(voucher.status)" />
                  {{ statusLabel(voucher.status) }}
                </span>
                <span hlmBadge variant="outline">
                  {{ isProductVoucher(voucher) ? 'Produkt' : 'Wert' }}
                </span>
                @for (name of getRedeemingMemberNames(voucher); track name) {
                  <span hlmBadge variant="secondary">{{ name }}</span>
                }
              </div>
            </div>
            <div hlmItemActions class="text-right">
              <p class="font-medium tabular-nums">{{ formatAmount(voucher.amount) }} €</p>
              <p class="text-muted-foreground text-xs tabular-nums">
                von {{ formatAmount(voucher.initial_amount) }} €
              </p>
            </div>
          </button>
        }
      </div>
    </div>

    <!-- Desktop table -->
    <div hlmTableContainer class="hidden md:block">
      <table hlmTable>
        <thead hlmTHead>
          <tr hlmTr>
            <th hlmTh>
              <button
                hlmBtn
                variant="ghost"
                size="xs"
                class="-ms-2"
                (click)="handleSort('created_at')">
                Datum
                <ng-icon [name]="sortIcon('created_at')" />
              </button>
            </th>
            <th hlmTh>
              <button hlmBtn variant="ghost" size="xs" class="-ms-2" (click)="handleSort('code')">
                Gutschein
                <ng-icon [name]="sortIcon('code')" />
              </button>
            </th>
            <th hlmTh>Typ</th>
            <th hlmTh class="text-right">
              <button
                hlmBtn
                variant="ghost"
                size="xs"
                class="-me-2"
                (click)="handleSort('initial_amount')">
                Ursprungswert
                <ng-icon [name]="sortIcon('initial_amount')" />
              </button>
            </th>
            <th hlmTh class="text-right">
              <button hlmBtn variant="ghost" size="xs" class="-me-2" (click)="handleSort('amount')">
                Aktueller Wert
                <ng-icon [name]="sortIcon('amount')" />
              </button>
            </th>
            <th hlmTh>
              <button
                hlmBtn
                variant="ghost"
                size="xs"
                class="-ms-2"
                (click)="handleSort('recipient_email')">
                Kunde
                <ng-icon [name]="sortIcon('recipient_email')" />
              </button>
            </th>
            <th hlmTh>
              <button hlmBtn variant="ghost" size="xs" class="-ms-2" (click)="handleSort('status')">
                Status
                <ng-icon [name]="sortIcon('status')" />
              </button>
            </th>
            @if (hasRedeemingMemberColumn) {
              <th hlmTh>Eingelöst von</th>
            }
          </tr>
        </thead>

        <tbody hlmTBody>
          @for (voucher of vouchers; track voucher.id) {
            <tr hlmTr class="cursor-pointer" (click)="viewVoucherDetails(voucher.code)">
              <td hlmTd class="whitespace-nowrap">{{ formatDate(voucher.created_at) }}</td>

              <td hlmTd>
                <span class="font-mono font-medium">{{ voucher.code || '–' }}</span>
              </td>

              <td hlmTd>
                <div class="flex items-center gap-1.5">
                  <span hlmBadge variant="outline">
                    {{ isProductVoucher(voucher) ? 'Produkt' : 'Wert' }}
                  </span>
                  @if (voucher.voucher_product_data?.name) {
                    <span hlmBadge variant="secondary" class="max-w-32">
                      <span class="truncate">{{ voucher.voucher_product_data.name }}</span>
                    </span>
                  }
                </div>
              </td>

              <td hlmTd class="text-muted-foreground text-right tabular-nums">
                {{ formatAmount(voucher.initial_amount) }} €
              </td>
              <td hlmTd class="text-right font-medium tabular-nums">
                {{ formatAmount(voucher.amount) }} €
              </td>
              <td hlmTd class="text-muted-foreground">
                <span
                  class="block max-w-56 truncate"
                  [hlmTooltip]="voucher.recipient_email"
                  [tooltipDisabled]="!voucher.recipient_email">
                  {{ voucher.recipient_email }}
                </span>
              </td>

              <td hlmTd>
                <span hlmBadge variant="secondary">
                  <ng-icon [name]="statusIcon(voucher.status)" />
                  {{ statusLabel(voucher.status) }}
                </span>
              </td>

              @if (hasRedeemingMemberColumn) {
                <td hlmTd>
                  <div class="flex flex-wrap gap-1">
                    @for (name of getRedeemingMemberNames(voucher); track name) {
                      <span hlmBadge variant="secondary">{{ name }}</span>
                    } @empty {
                      <span class="text-muted-foreground">–</span>
                    }
                  </div>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div hlmCardFooter class="flex-col gap-3 border-t pt-3 sm:flex-row sm:justify-between">
      <p class="text-muted-foreground text-xs">
        Seite {{ currentPage }} von {{ totalPages }} · {{ totalItems }} Gutscheine
      </p>
      @if (totalPages > 1) {
        <app-table-pagination
          class="w-auto"
          [page]="currentPage"
          [totalPages]="totalPages"
          (pageChange)="handlePageChange($event)" />
      }
    </div>
  }
</div>

<ng-template #sortMenu>
  <hlm-dropdown-menu class="w-48">
    <hlm-dropdown-menu-label>Sortieren nach</hlm-dropdown-menu-label>
    <hlm-dropdown-menu-group>
      @for (option of sortFieldOptions; track option.value) {
        <button hlmDropdownMenuItem (click)="handleMobileSortField(option.value)">
          {{ option.label }}
          @if (currentSortField === option.value) {
            <ng-icon name="lucideCheck" class="ms-auto" />
          }
        </button>
      }
    </hlm-dropdown-menu-group>
    <div hlmDropdownMenuSeparator></div>
    <hlm-dropdown-menu-group>
      @for (option of sortOrderOptions; track option.value) {
        <button hlmDropdownMenuItem (click)="handleMobileSortOrder(option.value)">
          {{ option.label }}
          @if (currentSortOrder === option.value) {
            <ng-icon name="lucideCheck" class="ms-auto" />
          }
        </button>
      }
    </hlm-dropdown-menu-group>
  </hlm-dropdown-menu>
</ng-template>

<hlm-dialog [state]="showModal ? 'open' : 'closed'" (stateChanged)="onDialogState($event)">
  <hlm-dialog-content
    *hlmDialogPortal
    class="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto sm:max-w-2xl">
    <div hlmDialogHeader>
      <h3 hlmDialogTitle>Gutschein-Details</h3>
    </div>
    @if (selectedVoucherId) {
      <app-voucher-details [voucherCode]="selectedVoucherId" />
    }
  </hlm-dialog-content>
</hlm-dialog>
